import type { AwardTier } from '@shared/seasons';
import { CROWN_GLYPH } from '../../components/currencyGlyphs';

/**
 * Visual prestige bucket for an award tier, driving the badge's colour. The
 * threshold ladder is dynamic, so we bucket by cut-off rather than by ladder
 * index (which shifts with population): Champion stands alone, the podium
 * (top 2-3) and elite (top 5-10) tiers get hot metals, and the broad
 * gold/silver/bronze bands cover everything above.
 */
export type AwardTierKey = 'champion' | 'podium' | 'elite' | 'gold' | 'silver' | 'bronze';

export function awardTierKey(threshold: number): AwardTierKey {
  if (threshold <= 1) {
    return 'champion';
  }
  if (threshold <= 3) {
    return 'podium';
  }
  if (threshold <= 10) {
    return 'elite';
  }
  if (threshold <= 100) {
    return 'gold';
  }
  if (threshold <= 1000) {
    return 'silver';
  }
  return 'bronze';
}

/** Medallion content: a crown for the champion, else the "Top N" cut-off number. */
export function awardMedallion(tier: AwardTier): string {
  return tier.threshold === 1 ? CROWN_GLYPH : tier.threshold.toLocaleString();
}

/**
 * Emphasis for an award badge in the status card: `current` is the tier you'll
 * get if the season ended now, `next` is the goal one rung up.
 */
export type AwardState = 'current' | 'next';
